import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  ButtonGroup,
  FormController,
  FormLabel,
  RadioGroup,
  DrawerBody,
  DrawerFooter,
  SkeletonBlock,
  Typography
} from '@one-thd/sui-atomic-components';
import { InfoFilled } from '@one-thd/sui-icons';
import { useAddToQuote, useCreateQuote } from '../hooks/useQuotes';
import { DeliveryZipCodeDisplay } from './DeliveryZipCodeDisplay';
import { CreateNewQuoteCard } from './CreateNewQuoteCard';
import { Quotes } from '../shared/Quotes';
import { ProductInfo } from '../shared/ProductInfo';
import { useCustomerInformation } from '../hooks/useCustomerInformation';
import {
  triggerCloseQuoteAnalytics, triggerErrorAnalytics, triggerQuoteAddAnalytics, getQuoteRadioValue
} from '../../utils/helpers';
import {
  DEFAULT_SELECTED_QUOTE, DELIMITER, DELIVERY_FULFILLMENT, FULFILLMENT, NEW_QUOTE_OPTION, QUOTE_SELECTION_RADIO_GROUP_NAME, SCREENS,
  ADD_TO_QUOTE_X_OR_CANCEL_BUTTON
} from '../shared/Constants';

const AddToQuoteFormDrawer = ({
  location,
  onClose,
  products,
  quoteList,
  quotesPageSize,
  isQuoteListLoading = false,
  selectedQuote,
  setDeliveryZip,
  setError,
  setQuoteId,
  setQuoteName,
  setQuotesPageSize,
  setSelectedQuote,
  showLoadMore,
  projectQuoteData = {},
  productExceptions = [],
  setQuoteOriginAnalytic,
  setScreen,
  quoteTimeTrackingRef,
  showNewATQUI = false
}) => {
  const customerInfo = useCustomerInformation();
  const { svocId } = customerInfo;
  const { deliveryZip, localStoreId, applianceDeliveryStore } = location || {};

  const showZipCode = products?.some((item) => DELIVERY_FULFILLMENT.includes(item?.fulfillmentMethod)
  );
  const isApplianceItem = products?.some((item) => item?.fulfillmentMethod === FULFILLMENT.APPLIANCE);
  const [isDisabled, setDisabled] = useState(showZipCode && !deliveryZip);
  const [newQuoteName, setNewQuoteName] = useState('');
  const [newQuoteNameError, setNewQuoteNameError] = useState(null);
  const { projectId, projectZipCode } = projectQuoteData || {};

  const { CREATE_NEW_QUOTE, ERROR_SCREEN, SUCCESS } = SCREENS;

  const [addToQuote, quoteResponse] = useAddToQuote({
    deliveryZip,
    items: products,
    localStoreId,
    applianceDeliveryStore,
    quoteId: selectedQuote?.quoteId,
    svocId,
    userId: selectedQuote?.userId,
    projectId
  });
  const { loading, data, error } = quoteResponse;

  const isNewQuoteSelected = selectedQuote?.quoteId === NEW_QUOTE_OPTION;

  const [createQuote, createQuoteResponse] = useCreateQuote({
    items: products,
    ...customerInfo,
    quoteName: newQuoteName,
    localStoreId,
    applianceDeliveryStore,
    deliveryZip,
    isNew: true,
    projectId
  });
  const { loading: createLoading, data: createData, error: createError } = createQuoteResponse;

  const addItemToQuote = () => {
    addToQuote();
  };

  const createANewQuote = () => {
    createQuote();
  };

  const preselectNewQuote = () => {
    setSelectedQuote({ quoteId: NEW_QUOTE_OPTION, userId: '', quoteName: '' });
  };

  const selectNewQuoteOption = () => {
    preselectNewQuote();
    window.LIFE_CYCLE_EVENT_BUS?.trigger('add-to-quote.createAQuoteOverlay');
  };

  const deselectQuote = () => {
    setSelectedQuote(DEFAULT_SELECTED_QUOTE);
  };

  const triggerAnalytics = () => {
    triggerQuoteAddAnalytics('add-to-quote.quoteAdd', {
      quoteId: data?.addToQuote?.cartId,
      startTime: quoteTimeTrackingRef?.current?.startTime,
      products,
      productExceptions
    });
  };

  const triggerNewQuoteAnalytics = () => {
    triggerQuoteAddAnalytics('add-to-quote.newQuoteAdd', {
      quoteId: createData?.createQuote?.cartId,
      startTime: quoteTimeTrackingRef?.current?.startTime,
      products,
      productExceptions
    });
  };

  const selectQuote = (evt) => {
    const { value } = evt?.target;
    const [quoteId, userId, quoteName] = value.split(DELIMITER);
    setSelectedQuote({
      userId,
      quoteId,
      quoteName
    });
  };

  const createNewQuoteClick = () => {
    setScreen(CREATE_NEW_QUOTE);
    setSelectedQuote(DEFAULT_SELECTED_QUOTE);
    window.LIFE_CYCLE_EVENT_BUS?.trigger('add-to-quote.createAQuoteOverlay');
  };

  const handleClose = () => {
    onClose();
    triggerCloseQuoteAnalytics(ADD_TO_QUOTE_X_OR_CANCEL_BUTTON);
  };

  useEffect(() => {
    // Default selection on open: the drawer already fired addToQuoteOverlay, so don't also
    // fire createAQuoteOverlay here - that event is only for the shopper picking the option.
    if (showNewATQUI && !selectedQuote?.quoteId) {
      preselectNewQuote();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (data && !loading && !error) {
      setQuoteName(selectedQuote?.quoteName);
      setScreen(SUCCESS);
      triggerAnalytics();
      setQuoteOriginAnalytic('add-to-quote');
    } else if (error) {
      triggerErrorAnalytics(error);
      setError(error);
      setScreen(ERROR_SCREEN);
    }
  }, [data, loading, error]);

  useEffect(() => {
    if (createData && !createLoading && !createError) {
      setQuoteName(newQuoteName);
      setQuoteId(createData?.createQuote?.cartId);
      setScreen(SUCCESS);
      triggerNewQuoteAnalytics();
      setQuoteOriginAnalytic('create-a-quote');
    } else if (createError) {
      triggerErrorAnalytics(createError);
      setError(createError);
      setScreen(ERROR_SCREEN);
    }
  }, [createData, createLoading, createError]);

  if (loading || createLoading) {
    return (
      <DrawerBody>
        <SkeletonBlock />
      </DrawerBody>
    );
  }

  const selectionContent = (
    <>
      {showNewATQUI ? (
        <CreateNewQuoteCard
          selected={isNewQuoteSelected}
          onSelect={selectNewQuoteOption}
          onQuoteNameChange={(value, validationError) => {
            setNewQuoteName(value);
            setNewQuoteNameError(validationError);
          }}
        />
      ) : (
        <Button
          className="sui-mb-4 sui-mt-6 sui-text-base sui-flex"
          variant="text"
          onClick={createNewQuoteClick}
          data-testid="create-quote"
          disabled={isDisabled}
        >
          <u>Create New Quote</u>
        </Button>
      )}
      {!showNewATQUI && <div className="sui-pb-4 sui-text-base">-or-</div>}
      <FormController>
        {(showNewATQUI && quoteList?.length > 0) && (
          <>
            <Typography weight="bold" component="span" id="quote-names">
              Add to an Existing Quote
            </Typography>
            <div className="sui-mt-1 sui-mb-3">
              <Typography height="tight" variant="body-sm">
                Prices will update when adding items to or changing the ZIP Code of an existing quote.
              </Typography>
            </div>
          </>
        )}
        {(!showNewATQUI && quoteList?.length > 0) &&
         (
          <>
            <FormLabel id="quote-names" className="sui-cursor-default sui-text-base">
              Add to an Existing Quote:
            </FormLabel>
            <div className="sui-pt-4 sui-pr-2 sui-flex">
              <div className="sui-pr-2 sui-pt-1">
                <InfoFilled color="info" size="small" />
              </div>
              <Typography variant="body-sm" color="info">
                Prices will update when adding items to or changing the ZIP Code of an existing quote
              </Typography>
            </div>
          </>
        )}
        <Quotes
          quoteList={quoteList}
          selectedQuote={selectedQuote}
          selectQuote={selectQuote}
          showLoadMore={showLoadMore}
          quotesPageSize={quotesPageSize}
          setQuotesPageSize={setQuotesPageSize}
          showNewATQUI={showNewATQUI}
          isApplianceItem={isApplianceItem}
          isQuoteListLoading={isQuoteListLoading}
          deselectQuote={deselectQuote}
        />
      </FormController>
    </>
  );

  // New UI: one labeled radio group spans the New Quote card and the existing-quote cards, so
  // assistive tech sees a single choice set rather than two separate groups.
  const handleQuoteSelectionChange = (evt) => {
    if (evt?.target?.value === NEW_QUOTE_OPTION) {
      selectNewQuoteOption();
    } else {
      selectQuote(evt);
    }
  };

  const selectedExistingQuote = quoteList?.find((quote) => quote.cartId === selectedQuote?.quoteId);
  const selectionGroupValue = isNewQuoteSelected
    ? NEW_QUOTE_OPTION
    : (selectedExistingQuote && getQuoteRadioValue(selectedExistingQuote)) || '';

  return (
    <>
      <DrawerBody>
        <ProductInfo products={products} storeId={localStoreId} />

        <div className="sui-my-6 sui-border-b-1 sui-border-primary sui-border-solid" />

        {showZipCode && !projectZipCode && (
          <DeliveryZipCodeDisplay
            deliveryZip={deliveryZip}
            setDeliveryZip={setDeliveryZip}
            setDisabled={setDisabled}
            showNewATQUI={showNewATQUI}
          />
        )}
        {showZipCode && projectZipCode && (
          <div className="sui-mb-6">
            <Typography>Delivery ZIP Code</Typography>
            <Typography weight="bold">{projectZipCode}</Typography>
          </div>
        )}
        {showNewATQUI ? (
          <RadioGroup
            aria-label="Select a quote"
            name={QUOTE_SELECTION_RADIO_GROUP_NAME}
            value={selectionGroupValue}
            onChange={handleQuoteSelectionChange}
          >
            {selectionContent}
          </RadioGroup>
        ) : selectionContent}
      </DrawerBody>
      <DrawerFooter>
        <ButtonGroup orientation="vertical">
          <Button
            fullWidth
            variant="primary"
            onClick={isNewQuoteSelected ? createANewQuote : addItemToQuote}
            disabled={isNewQuoteSelected
              ? (!newQuoteName?.length || !!newQuoteNameError || isDisabled)
              : (!selectedQuote?.quoteId?.length || isDisabled)}
            data-testid={isNewQuoteSelected ? 'create-new-quote' : 'add-to-existing-quote'}
          >
            Add to Quote
          </Button>
          <Button fullWidth variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
        </ButtonGroup>
      </DrawerFooter>
    </>
  );
};

AddToQuoteFormDrawer.propTypes = {
  location: PropTypes.shape({
    deliveryZip: PropTypes.string,
    localStoreId: PropTypes.string
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      itemId: PropTypes.string.isRequired,
      quantity: PropTypes.number.isRequired,
      fulfillmentLocation: PropTypes.string.isRequired,
      fulfillmentMethod: PropTypes.string.isRequired,
      identifiers: PropTypes.shape({
        brandName: PropTypes.string,
        productLabel: PropTypes.string
      }).isRequired,
      pricing: PropTypes.shape({
        value: PropTypes.number
      }).isRequired,
      media: PropTypes.shape({
        images: PropTypes.arrayOf(
          PropTypes.shape({
            altText: PropTypes.string,
            sizes: PropTypes.arrayOf(PropTypes.string),
            subType: PropTypes.string,
            type: PropTypes.string,
            url: PropTypes.string
          })
        )
      })
    })
  ).isRequired,
  quoteList: PropTypes.arrayOf(
    PropTypes.shape({
      quoteId: PropTypes.string,
      quoteName: PropTypes.string,
      userId: PropTypes.string
    })
  ).isRequired,
  quotesPageSize: PropTypes.number.isRequired,
  isQuoteListLoading: PropTypes.bool,
  selectedQuote: PropTypes.shape({
    quoteId: PropTypes.string,
    quoteName: PropTypes.string,
    userId: PropTypes.string
  }).isRequired,
  setDeliveryZip: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
  setQuoteId: PropTypes.func.isRequired,
  setQuoteName: PropTypes.func.isRequired,
  setQuotesPageSize: PropTypes.func.isRequired,
  setSelectedQuote: PropTypes.func.isRequired,
  setScreen: PropTypes.func.isRequired,
  showLoadMore: PropTypes.bool.isRequired,
  projectQuoteData: PropTypes.shape({
    projectId: PropTypes.string,
    projectZipCode: PropTypes.string,
    projectName: PropTypes.string
  }),
  productExceptions: PropTypes.arrayOf(
    PropTypes.shape({
      flag: PropTypes.string.isRequired,
      fulfillment: PropTypes.shape({}),
      identifiers: PropTypes.shape({}),
      pricing: PropTypes.shape({}),
      itemId: PropTypes.string,
      id: PropTypes.string,
      quantity: PropTypes.number
    })
  ),
  setQuoteOriginAnalytic: PropTypes.func.isRequired,
  quoteTimeTrackingRef: PropTypes.shape({
    current: PropTypes.shape({
      startTime: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])])
    })
  }).isRequired,
  showNewATQUI: PropTypes.bool
};

AddToQuoteFormDrawer.displayName = 'AddToQuoteFormDrawer';

export { AddToQuoteFormDrawer };
